import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarPlus,
  Clock,
  Spinner,
  Trash,
  CalendarCheck,
  ArrowLeft,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

function fmtSlot(iso) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AddSlotForm({ onAdded }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(todayStr);
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Pick a date and time.");
      return;
    }
    const local = new Date(`${date}T${time}`);
    if (isNaN(local.getTime())) {
      toast.error("Invalid date/time.");
      return;
    }
    if (local.getTime() < Date.now() + 60 * 1000) {
      toast.error("Pick a time at least a minute in the future.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/availability", {
        starts_at: local.toISOString(),
        duration_min: Number(duration),
      });
      toast.success("Slot published.");
      setTime("");
      onAdded(data);
    } catch (err) {
      toast.error(apiError(err, "Could not add slot"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E8E4DB] rounded-2xl p-6 sm:p-7 shadow-sm">
      <div className="label-kicker mb-2">Publish a new slot</div>
      <h3 className="font-serif text-2xl text-[#1E3A2B]">Add availability</h3>
      <p className="text-sm text-[#4A524C] mt-2">Leaders will only see open future slots.</p>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <span className="label-kicker block mb-2">Date</span>
          <input
            type="date"
            value={date}
            min={todayStr}
            onChange={(e) => setDate(e.target.value)}
            data-testid="coach-slot-date"
            className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:outline-none rounded-xl px-4 py-3 text-[#121815]"
          />
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Start time</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            data-testid="coach-slot-time"
            className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:outline-none rounded-xl px-4 py-3 text-[#121815]"
          />
        </label>
        <label className="block">
          <span className="label-kicker block mb-2">Duration (min)</span>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            data-testid="coach-slot-duration"
            className="w-full bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:outline-none rounded-xl px-4 py-3 text-[#121815]"
          >
            <option value={15}>15</option>
            <option value={30}>30</option>
            <option value={45}>45</option>
            <option value={60}>60</option>
            <option value={90}>90</option>
          </select>
        </label>
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="submit"
          disabled={busy}
          data-testid="coach-slot-add-button"
          className="inline-flex items-center gap-2 bg-[#1E3A2B] text-white px-6 py-2.5 rounded-xl hover:bg-[#2C4C3B] disabled:opacity-60"
        >
          {busy ? <Spinner size={16} className="animate-spin" /> : <CalendarPlus size={16} weight="duotone" />}
          Publish slot
        </button>
      </div>
    </form>
  );
}

function SlotRow({ slot, onDelete }) {
  const isBooked = slot.status === "booked";
  return (
    <div
      data-testid={`slot-row-${slot.id}`}
      className="flex items-center justify-between gap-3 bg-white border border-[#E8E4DB] rounded-xl px-4 py-3 hover:border-[#1E3A2B]/40 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
            isBooked ? "bg-[#C77D5E]/15 text-[#C77D5E]" : "bg-[#1E3A2B]/10 text-[#1E3A2B]"
          }`}
        >
          {isBooked ? <CalendarCheck size={18} weight="duotone" /> : <Clock size={18} weight="duotone" />}
        </div>
        <div className="min-w-0">
          <div className="font-medium text-[#121815] truncate">{fmtSlot(slot.starts_at)}</div>
          <div className="text-xs text-[#8A948E] mt-0.5">
            {slot.duration_min} min ·{" "}
            {isBooked ? (
              <span className="text-[#C77D5E] font-medium">Booked</span>
            ) : (
              <span className="text-[#1E3A2B] font-medium">Open</span>
            )}
            {isBooked && slot.booked_thread_id && (
              <>
                {" · "}
                <Link
                  to={`/coach/thread/${slot.booked_thread_id}`}
                  className="underline hover:text-[#1E3A2B]"
                >
                  open thread
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
      {!isBooked && (
        <button
          onClick={() => onDelete(slot)}
          data-testid={`slot-delete-${slot.id}`}
          className="text-[#8A948E] hover:text-[#C77D5E] p-2 rounded-lg hover:bg-[#F3F0EA]"
          aria-label="Delete slot"
        >
          <Trash size={16} weight="duotone" />
        </button>
      )}
    </div>
  );
}

export default function CoachAvailability() {
  const { coach } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coach/availability/all");
      setSlots(data.slots || []);
    } catch (err) {
      toast.error(apiError(err, "Could not load slots"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (coach) refresh();
  }, [coach]);

  const handleAdded = (slot) => {
    setSlots((prev) => [...prev, slot].sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)));
  };

  const handleDelete = async (slot) => {
    if (!confirm(`Delete the ${fmtSlot(slot.starts_at)} slot?`)) return;
    try {
      await api.delete(`/coach/availability/${slot.id}`);
      setSlots((prev) => prev.filter((s) => s.id !== slot.id));
      toast.success("Slot removed.");
    } catch (err) {
      toast.error(apiError(err, "Could not delete slot"));
    }
  };

  const { upcomingOpen, booked, past } = useMemo(() => {
    const now = Date.now();
    const upcomingOpen = [];
    const booked = [];
    const past = [];
    for (const s of slots) {
      const t = new Date(s.starts_at).getTime();
      if (s.status === "booked") booked.push(s);
      else if (t < now) past.push(s);
      else upcomingOpen.push(s);
    }
    return { upcomingOpen, booked, past };
  }, [slots]);

  if (coach === null) {
    return (
      <div className="text-center text-[#8A948E] py-16 flex items-center justify-center gap-2">
        <Spinner size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!coach) return <Navigate to="/coach/login" state={{ from: "/coach/availability" }} replace />;

  return (
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
      <div className="anim-in delay-1 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link
            to="/coach/dashboard"
            className="inline-flex items-center gap-1.5 text-sm text-[#8A948E] hover:text-[#1E3A2B] mb-3"
          >
            <ArrowLeft size={14} weight="bold" /> Dashboard
          </Link>
          <div className="label-kicker" style={{ color: "#1E3A2B" }}>Coach Console</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
            Your availability.
          </h1>
          <p className="text-[#4A524C] mt-3 leading-relaxed">
            Publish times — leaders pick from this list when scheduling a live conversation.
          </p>
        </div>
      </div>

      <div className="mt-10 anim-in delay-2">
        <AddSlotForm onAdded={handleAdded} />
      </div>

      <section className="mt-10 anim-in delay-3">
        <div className="label-kicker mb-3">Open · upcoming</div>
        {loading ? (
          <div className="text-[#8A948E] text-sm flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Loading…
          </div>
        ) : upcomingOpen.length === 0 ? (
          <div className="text-[#8A948E] text-sm bg-[#F3F0EA] rounded-xl px-4 py-6 text-center">
            No open slots yet. Publish one above and leaders can book it.
          </div>
        ) : (
          <div className="space-y-2">
            {upcomingOpen.map((s) => (
              <SlotRow key={s.id} slot={s} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </section>

      {booked.length > 0 && (
        <section className="mt-10">
          <div className="label-kicker mb-3">Booked</div>
          <div className="space-y-2">
            {booked.map((s) => (
              <SlotRow key={s.id} slot={s} onDelete={handleDelete} />
            ))}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section className="mt-10">
          <div className="label-kicker mb-3">Past · open (expired)</div>
          <div className="space-y-2">
            {past.map((s) => (
              <SlotRow key={s.id} slot={s} onDelete={handleDelete} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
